import { useState, useEffect } from "react";
import { useTenants } from "../../../api/useTenants";
import { usePlanTemplatesMerged } from "../../../api/usePlanTemplatesMerged";
import type { TenantPlan } from "../../../api/types";
import { apiGet } from "../../../api/http";

type BillStatus = "paid" | "pending" | "overdue" | "refunded";

interface BillingRecord {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantCode: string;
  logoColor: string;
  plan: TenantPlan;
  amount: number;
  type: "new" | "renewal" | "upgrade" | "refund";
  status: BillStatus;
  period: string;
  paidAt?: string;
  dueDate: string;
  invoiceNo: string;
  notes?: string;
}

const statusConfig: Record<BillStatus, { label: string; color: string; dot: string }> = {
  paid: { label: "已付款", color: "bg-green-50 text-green-700", dot: "bg-green-500" },
  pending: { label: "待付款", color: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  overdue: { label: "已逾期", color: "bg-red-50 text-red-600", dot: "bg-red-500" },
  refunded: { label: "已退款", color: "bg-gray-100 text-gray-500", dot: "bg-gray-400" },
};

const typeConfig: Record<BillingRecord["type"], { label: string; color: string }> = {
  new: { label: "新开通", color: "text-green-600 bg-green-50" },
  renewal: { label: "续费", color: "text-amber-600 bg-amber-50" },
  upgrade: { label: "升级套餐", color: "text-red-600 bg-red-50" },
  refund: { label: "退款", color: "text-gray-500 bg-gray-100" },
};

export default function BillingRecords() {
  const { tenants: tenantList } = useTenants();
  const { mergedByPlan } = usePlanTemplatesMerged();
  const [billingRecords, setBillingRecords] = useState<BillingRecord[]>([]);
  const [listState, setListState] = useState<"loading" | "ok" | "error">("loading");
  const [filterStatus, setFilterStatus] = useState<"all" | BillStatus>("all");
  const [filterTenant, setFilterTenant] = useState<string>("all");
  const [selectedRecord, setSelectedRecord] = useState<BillingRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiGet<BillingRecord[]>("/api/billing-records")
      .then((rows) => {
        if (!cancelled) {
          setBillingRecords(rows);
          setListState("ok");
        }
      })
      .catch(() => {
        if (!cancelled) setListState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = billingRecords
    .filter((b) => filterStatus === "all" || b.status === filterStatus)
    .filter((b) => filterTenant === "all" || b.tenantId === filterTenant)
    .filter((b) =>
      !searchQuery ||
      b.tenantName.includes(searchQuery) ||
      b.invoiceNo.includes(searchQuery) ||
      b.tenantCode.includes(searchQuery)
    );

  const totalRevenue = billingRecords.filter((b) => b.status === "paid").reduce((s, b) => s + b.amount, 0);
  const pendingRevenue = billingRecords.filter((b) => b.status === "pending").reduce((s, b) => s + b.amount, 0);
  const overdueRevenue = billingRecords.filter((b) => b.status === "overdue").reduce((s, b) => s + b.amount, 0);

  const tenantRenewalMap: Record<string, { name: string; expiresAt: string; status: string; logoColor: string; plan: TenantPlan }> = {};
  tenantList.forEach((t) => {
    tenantRenewalMap[t.id] = {
      name: t.name,
      expiresAt: t.expiresAt,
      status: t.status,
      logoColor: t.logoColor,
      plan: t.plan,
    };
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">收费记录</h2>
          <p className="text-xs text-gray-400 mt-0.5">查看所有租户付款历史和续费状态</p>
          {listState === "loading" && <p className="text-xs text-amber-600 mt-1">加载账单中…</p>}
          {listState === "error" && (
            <p className="text-xs text-red-500 mt-1">无法加载账单，请确认 API 可用。</p>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: "累计营收", value: `¥${totalRevenue.toLocaleString()}`, icon: "ri-money-cny-circle-line", color: "text-green-600 bg-green-50", sub: "已收款合计" },
          { label: "待收款", value: `¥${pendingRevenue.toLocaleString()}`, icon: "ri-time-line", color: "text-amber-600 bg-amber-50", sub: "等待付款" },
          { label: "逾期未付", value: `¥${overdueRevenue.toLocaleString()}`, icon: "ri-error-warning-line", color: "text-red-600 bg-red-50", sub: "需催收" },
          { label: "账单总数", value: billingRecords.length.toString(), icon: "ri-bill-line", color: "text-gray-600 bg-gray-100", sub: "全部记录" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-800">{s.value}</p>
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Renewal Status Section */}
      <div className="bg-white rounded-2xl p-5 mb-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-refresh-line text-amber-500"></i> 租户续费状态
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {tenantList.map((t) => {
            const plan = mergedByPlan[t.plan];
            const expiryDate = new Date(t.expiresAt);
            const today = new Date();
            const daysLeft = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            const urgency = daysLeft < 0 ? "expired" : daysLeft < 30 ? "urgent" : daysLeft < 90 ? "soon" : "ok";
            const urgencyStyle = {
              expired: "border-red-200 bg-red-50",
              urgent: "border-amber-200 bg-amber-50",
              soon: "border-yellow-100 bg-yellow-50",
              ok: "border-gray-100 bg-white",
            }[urgency];
            return (
              <div key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border ${urgencyStyle}`}>
                <div className={`w-8 h-8 ${t.logoColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <i className="ri-building-4-line text-white text-xs"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{t.shortName}</p>
                  <p className={`text-xs ${urgency === "expired" ? "text-red-500" : urgency === "urgent" ? "text-amber-600" : "text-gray-400"}`}>
                    {urgency === "expired"
                      ? `已过期 ${Math.abs(daysLeft)} 天`
                      : urgency === "urgent"
                      ? `${daysLeft} 天后到期`
                      : urgency === "soon"
                      ? `${daysLeft} 天后到期`
                      : `${t.expiresAt} 到期`}
                  </p>
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${plan.bgColor} ${plan.color} flex-shrink-0`}>
                  {plan.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索租户、发票号..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400"
          />
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {(["all", "paid", "pending", "overdue", "refunded"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${
                filterStatus === s ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {s === "all" ? "全部" : statusConfig[s as BillStatus].label}
            </button>
          ))}
        </div>
        <select
          value={filterTenant}
          onChange={(e) => setFilterTenant(e.target.value)}
          className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-amber-400 cursor-pointer"
        >
          <option value="all">全部租户</option>
          {tenantList.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="flex gap-5">
        <div className="flex-1 min-w-0 bg-white rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">租户</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">发票号</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">类型</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">套餐</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">金额</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">状态</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">付款/到期</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((record) => {
                const plan = mergedByPlan[record.plan];
                const status = statusConfig[record.status];
                const type = typeConfig[record.type];
                return (
                  <tr
                    key={record.id}
                    onClick={() => setSelectedRecord(record)}
                    className={`hover:bg-gray-50/50 cursor-pointer transition-colors ${
                      selectedRecord?.id === record.id ? "bg-amber-50/40" : ""
                    }`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 ${record.logoColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <i className="ri-building-4-line text-white text-xs"></i>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-800">{record.tenantName}</p>
                          <p className="text-xs text-gray-400 font-mono">{record.tenantCode}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{record.invoiceNo}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${type.color}`}>{type.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${plan.bgColor} ${plan.color}`}>{plan.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-bold ${record.amount === 0 ? "text-gray-400" : record.status === "refunded" ? "text-gray-400 line-through" : "text-gray-800"}`}>
                        {record.amount === 0 ? "免费" : `¥${record.amount.toLocaleString()}`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${status.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {record.paidAt
                        ? record.paidAt
                        : record.status === "overdue"
                        ? <span className="text-amber-600 font-medium">逾期: {record.dueDate}</span>
                        : <span>到期 {record.dueDate}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-bill-line text-4xl text-gray-200 block mb-2"></i>
              <p className="text-sm">暂无符合条件的记录</p>
            </div>
          )}
        </div>

        {/* Detail */}
        {selectedRecord && (
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl p-5 sticky top-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 text-sm">账单详情</h3>
                <button onClick={() => setSelectedRecord(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
                <div className={`w-9 h-9 ${selectedRecord.logoColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <i className="ri-building-4-line text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{selectedRecord.tenantName}</p>
                  <p className="text-xs text-gray-400 font-mono">{selectedRecord.tenantCode}</p>
                </div>
              </div>

              <div className="text-center mb-5 py-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-400 mb-1">账单金额</p>
                <p className={`text-3xl font-bold ${selectedRecord.amount === 0 ? "text-gray-400" : "text-gray-900"}`}>
                  {selectedRecord.amount === 0 ? "免费" : `¥${selectedRecord.amount.toLocaleString()}`}
                </p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig[selectedRecord.status].color} flex items-center gap-1`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[selectedRecord.status].dot}`}></span>
                    {statusConfig[selectedRecord.status].label}
                  </span>
                </div>
              </div>

              <div className="space-y-2.5 text-xs">
                {[
                  { label: "发票号", value: selectedRecord.invoiceNo },
                  { label: "账单类型", value: typeConfig[selectedRecord.type].label },
                  { label: "套餐", value: mergedByPlan[selectedRecord.plan].label },
                  { label: "服务期间", value: selectedRecord.period },
                  { label: "应付日期", value: selectedRecord.dueDate },
                  ...(selectedRecord.paidAt ? [{ label: "实付时间", value: selectedRecord.paidAt }] : []),
                  ...(selectedRecord.notes ? [{ label: "备注", value: selectedRecord.notes }] : []),
                ].map((item) => (
                  <div key={item.label} className="flex justify-between gap-2">
                    <span className="text-gray-400 flex-shrink-0">{item.label}</span>
                    <span className="text-gray-700 font-medium text-right">{item.value}</span>
                  </div>
                ))}
              </div>

              {selectedRecord.status === "pending" && (
                <button className="w-full mt-4 py-2.5 bg-amber-500 text-white text-xs rounded-xl hover:bg-amber-400 cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-mail-send-line mr-1.5"></i>发送催缴通知
                </button>
              )}
              {selectedRecord.status === "overdue" && (
                <div className="mt-4 space-y-2">
                  <button className="w-full py-2.5 bg-red-50 text-red-600 text-xs rounded-xl hover:bg-red-100 cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-alarm-warning-line mr-1.5"></i>紧急催缴
                  </button>
                  <button className="w-full py-2 bg-gray-100 text-gray-600 text-xs rounded-xl hover:bg-gray-200 cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-close-line mr-1.5"></i>标记为坏账
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
